/* eslint-disable react/prop-types */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import React, { useCallback, useState } from 'react';

import { Div, Icon, Tag, Text } from '@pinpoint/design-kit';

import { noOp, getModelTagStyles } from 'src/functions';
import CONSTANTS from 'src/store/constants';
import { Rbac } from 'src/containers';
import { ApproveRealtimeConfigDialog, DeleteRealtimeConfigDialog } from 'src/components/dialogs';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

export function CellDate({ value }) {
  return (
    <Text textWeight="400" textSize="small" data-tip={dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY')}>
      {dayjs(value).format('MMM DD, YYYY')}
    </Text>
  );
}

export function CellText({ value, ...props }) {
  return <Text {...props}>{value}</Text>;
}

export function CellTag({ value, row }) {
  const { predictionType, typeDetail, dataModelType } = value;
  const { score_type: scoreType } = row.original;
  const tagProps = {
    border: '1px solid',
    p: { x: '.375rem', y: '.188rem' },
    h: '1.125rem',
  };
  const iconProps = {
    h: '.75rem',
    m: { r: '.25rem' },
    size: '.75rem',
  };
  let displayText = CONSTANTS.REAL_TIME_API.CS_TYPE.static;
  if (scoreType === 'static') {
    tagProps.textColor = 'tagRtDemo';
    tagProps.borderColor = 'tagRtDemo';
    tagProps['data-tip'] = 'API Demonstration ("DEMO")';
    iconProps.color = 'tagRtDemo';
    iconProps.name = 'RTTest';
    iconProps.viewBox = '0 0 10 10';
  }
  let extraText = '';
  if (scoreType === 'model' && dataModelType === 'CLASSIFIER') {
    if (predictionType === 'Early Cancellation') extraText = `${predictionType}, 60-day window`;
    else if (['Other', 'Premium Leakage'].includes(predictionType)) extraText = `${predictionType}, ${typeDetail}`;
    else extraText = predictionType;
    const styleData = getModelTagStyles(dataModelType, extraText);
    tagProps.textColor = 'tagRtClassifier';
    tagProps.borderColor = 'tagRtClassifier';
    tagProps['data-tip'] = styleData.tip;
    iconProps.color = 'tagRtClassifier';
    iconProps.name = 'ClassifierModel';
    iconProps.viewBox = '0 0 10.46 16';
    iconProps.transform = 'rotate(-0.25turn)';
    displayText = dataModelType;
  }
  if (scoreType === 'model' && dataModelType.includes('ACT_')) {
    const styleData = getModelTagStyles(dataModelType, '');
    tagProps.textColor = styleData.color;
    tagProps.borderColor = styleData.color;
    tagProps['data-tip'] = styleData.tip;
    iconProps.color = styleData.color;
    iconProps.name = styleData.name;
    iconProps.viewBox = styleData.viewBox;
    displayText = CONSTANTS.TAL_MODEL.TAG_NAME[dataModelType];
  }

  return (
    <Tag {...tagProps}>
      <Icon {...iconProps} />
      <Text textSize="small">{displayText}</Text>
    </Tag>
  );
}

export function CellStatus({ value }) {
  switch (value) {
    case 1:
      return <Div rounded="sm" bg="marigold" h=".9rem" w=".9rem" data-tip="Pending approval" />;
    case 2:
      return <Div rounded="sm" bg="blueLight" h=".9rem" w=".9rem" data-tip="In progress" />;
    case 3:
      return <Div rounded="sm" bg="vividRed" h=".9rem" w=".9rem" data-tip="Errored" />;
    case 4:
      return <Div rounded="sm" bg="rioGrande" h=".9rem" w=".9rem" data-tip="Completed" />;
    default:
      return <Div rounded="sm" bg="talUnknown" h=".9rem" w=".9rem" />;
  }
}

export function CellActions({ value, approveConfig, deleteConfig, status }) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setDeleteModalOpen(true), [setDeleteModalOpen]);
  const closeDeleteModal = useCallback(() => setDeleteModalOpen(false), [setDeleteModalOpen]);
  const openApproveModal = useCallback(() => setApproveModalOpen(true), [setApproveModalOpen]);
  const closeApproveModal = useCallback(() => setApproveModalOpen(false), [setApproveModalOpen]);
  const handleDelete = useCallback(() => deleteConfig(value), [deleteConfig, value]);
  const handleApprove = useCallback(() => approveConfig(value), [approveConfig, value]);

  return (
    <>
      {status === CONSTANTS.REAL_TIME_API.CS_STATE.PENDING_APPROVAL ? (
        <Rbac
          roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES]}
        >
          <Icon
            name="Checked"
            cursor="pointer"
            hoverTransform="scale(1.25)"
            color="lightGrey"
            hoverColor="brand500"
            size=".75rem"
            m={{ r: '0.3125rem' }}
            onClick={openApproveModal}
            data-test="approve"
          />
          <ApproveRealtimeConfigDialog
            isOpen={approveModalOpen}
            actions={{
              close: {
                on: closeApproveModal,
              },
              cancel: {
                on: noOp,
              },
              handlers: [
                {
                  on: handleApprove,
                },
              ],
            }}
          />
        </Rbac>
      ) : null}
      <Rbac
        roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN]}
        fallback={
          <Icon size=".75rem" viewBox="0 0 50 50" m={{ x: '0' }} color="tabRest" name="Delete" cursor="not-allowed" />
        }
      >
        <Icon
          size=".75rem"
          color="tabSelected"
          hoverColor="danger800"
          viewBox="0 0 50 50"
          m={{ x: '0' }}
          name="Delete"
          cursor="pointer"
          data-test="delete"
          data-tip="Delete config"
          hoverTransform="scale(1.25)"
          onClick={openDeleteModal}
        />
        <DeleteRealtimeConfigDialog
          isOpen={deleteModalOpen}
          actions={{
            close: {
              on: closeDeleteModal,
            },
            cancel: {
              on: noOp,
            },
            handlers: [
              {
                on: handleDelete,
              },
            ],
          }}
        />
      </Rbac>
    </>
  );
}
